import React, { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../framework/customStore";
import {
    setConfirmation,
    showErrorMessage,
    showSuccessMessage,
} from "../../../models/store/storeActions";
import { ConfirmationDialogType } from "../../../models/store/storeTypes";
import { handleApiError } from "../../../models/store/storeEffects";
import * as formTemplateService from "../../../services/formTemplateService";
import { EditorField, FormEditorState } from "./types";
import {
    buildSchema,
    parseSchemaToFields,
    validateFields,
} from "./schemaBuilder";
import { EditorPanel } from "./editorPanel";
import { PreviewPanel } from "./previewPanel";
import { MuiSwitch } from "../../framework/muiSwitch";

interface FormEditorContentProps {
    templateId?: string;
    onCancel: () => void;
}

export const FormEditorContent = ({
    templateId: id,
    onCancel,
}: FormEditorContentProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [editorState, setEditorState] = useState<FormEditorState>({
        name: "",
        fields: [],
    });
    const [rowId, setRowId] = useState<string | undefined>();
    const [templateId, setTemplateId] = useState<string | undefined>(id);
    const [targetObjectType, setTargetObjectType] = useState<string>("");
    const [isActive, setIsActive] = useState(true);
    const [loading, setLoading] = useState(!!id);
    const [saving, setSaving] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [validationErrors, setValidationErrors] = useState<
        Map<string, string>
    >(new Map());

    useEffect(() => {
        if (!id) return;
        const abortController = new AbortController();
        formTemplateService
            .getFormTemplate(id, abortController.signal)
            .then((response) => {
                const parsed = parseSchemaToFields(response.data);
                setEditorState(parsed);
                setRowId(response.data.rowId);
                setTemplateId(response.data.id);
                setTargetObjectType(response.data.targetObjectType ?? "");
                setIsActive(response.data.isActive);
            })
            .catch((e) => {
                if (!abortController.signal.aborted) {
                    handleApiError(e, dispatch);
                }
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });
        return () => abortController.abort();
    }, [id, dispatch]);

    const handleFieldsChange = useCallback((fields: EditorField[]) => {
        setEditorState((prev) => ({ ...prev, fields }));
        setIsDirty(true);
    }, []);

    const handleSave = async() => {
        const errors = validateFields(editorState.fields);
        if (errors.length > 0) {
            setValidationErrors(
                new Map(errors.map((e) => [e.fieldId, e.error]))
            );
            dispatch(
                showErrorMessage(t("settings.formEditor.validationFailed"))
            );
            return;
        }
        setValidationErrors(new Map());
        setSaving(true);
        try {
            const schema = buildSchema(editorState);
            const response = await formTemplateService.saveFormTemplate({
                id: templateId,
                rowId,
                name: editorState.name,
                jsonSchema: JSON.stringify(schema),
                targetObjectType: targetObjectType || null,
                isActive,
            });
            setTemplateId(response.data.id);
            setRowId(response.data.rowId);
            setIsDirty(false);
            dispatch(showSuccessMessage(t("settings.formEditor.saveSuccess")));
        } catch (error: unknown) {
            console.error("Error saving form template:", error);
            const status =
                error && typeof error === "object" && "status" in error
                    ? (error as { status: number }).status
                    : undefined;
            dispatch(
                showErrorMessage(
                    status === 409
                        ? t("common.saveConflict")
                        : t("settings.formEditor.saveFailed")
                )
            );
        } finally {
            setSaving(false);
        }
    };

    const handleSaveRef = useRef(handleSave);
    handleSaveRef.current = handleSave;

    useEffect(() => {
        const handler = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "s") {
                e.preventDefault();
                void handleSaveRef.current();
            }
        };
        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, []);

    const handleBack = () => {
        if (isDirty) {
            dispatch(
                setConfirmation(
                    ConfirmationDialogType.Warning,
                    t("common.youHaveNotSavedChangesDoYouWantToSaveChanges"),
                    null,
                    () => {
                        void handleSave().then(() => onCancel());
                    },
                    () => onCancel(),
                    () => null
                )
            );
        } else {
            onCancel();
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    p: 2,
                    pb: 1,
                }}
            >
                <IconButton
                    size="small"
                    color="primary"
                    onClick={handleBack}
                    data-testid="form-editor-back"
                >
                    <ArrowBackIcon />
                </IconButton>
                <TextField
                    size="small"
                    label={t("settings.formEditor.formName")}
                    value={editorState.name}
                    onChange={(e) => {
                        setEditorState((prev) => ({
                            ...prev,
                            name: e.target.value,
                        }));
                        setIsDirty(true);
                    }}
                    sx={{ minWidth: 300 }}
                    data-testid="form-editor-name"
                />
                <TextField
                    select
                    size="small"
                    label={t("settings.formEditor.targetObjectType")}
                    value={targetObjectType}
                    onChange={(e) => {
                        setTargetObjectType(e.target.value);
                        setIsDirty(true);
                    }}
                    sx={{ minWidth: 200 }}
                    data-testid="form-editor-target-type"
                >
                    <MenuItem value="">
                        {t("settings.formEditor.targetObjectTypeNone")}
                    </MenuItem>
                    <MenuItem value="Vehicle">
                        {t("settings.formEditor.targetObjectTypeVehicle")}
                    </MenuItem>
                </TextField>
                <MuiSwitch
                    label={t("settings.formEditor.active")}
                    checked={isActive}
                    onChange={(_, checked) => {
                        setIsActive(checked);
                        setIsDirty(true);
                    }}
                    data-testid="form-editor-is-active"
                />
                <Box sx={{ flex: 1 }} />
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<CancelIcon />}
                    onClick={handleBack}
                    data-testid="form-editor-cancel"
                >
                    {t("settings.formEditor.cancel")}
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    size="small"
                    startIcon={<SaveIcon />}
                    onClick={() => {
                        void handleSave();
                    }}
                    disabled={saving || !editorState.name.trim() || editorState.fields.length === 0}
                    data-testid="form-editor-save"
                >
                    {t("settings.formEditor.save")}
                </Button>
            </Box>
            <Divider />
            <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
                <Box sx={{ flex: 1, borderRight: 1, borderColor: "divider" }}>
                    <EditorPanel
                        fields={editorState.fields}
                        onFieldsChange={handleFieldsChange}
                        validationErrors={validationErrors}
                    />
                </Box>
                <Box sx={{ flex: 1 }}>
                    <PreviewPanel editorState={editorState} />
                </Box>
            </Box>
        </Box>
    );
};
